import { useEffect, useRef, useState } from "react";
import "./flash.css";
import site from "./site";
import { PageContext, type Hours, type PageCtx, type SectionProps } from "./context";
import { applySeo } from "./seo";
import { ALTERNATING, RENDERERS } from "./sections";
import { fontsHref, isRtl, themeStyle } from "./theme";
import { str } from "./ui";
import type { Generated } from "./facts";
import FeedbackWidget from "./feedback-widget";
import Landing from "./landing";
import AppShell from "./app-shell";
import { useThemeMode } from "./theme-mode";
import { useAuth } from "./auth";
import { t } from "@/lib/i18n";

// Flash 站：整站内容都在 site.json 里（由生成流程写入），页面 = 按 page.types 的顺序渲染组件库里的组件。
// 想改文案/换图直接改 site.json；想改版式、加组件改 sections.tsx / ui.tsx；换气质换 theme.style(见 theme.ts)。

type Page = (typeof site.pages)[number];
type Site = {
  name?: string;
  lang?: string;
  formUrl?: string;
  ui?: PageCtx["ui"];
  theme?: { accent?: string; mode?: string; style?: string };
  nap?: { phone?: string; address?: string; generated?: Generated };
};
const meta = site as unknown as Site;

/** Витринные блоки: в рабочем пространстве они не нужны, иначе кажется, что вход не сработал. */
const MARKETING = new Set([
  "announcement",
  "navbar",
  "hero",
  "stats",
  "showcase",
  "features",
  "cases",
  "steps",
  "testimonials",
  "faq",
  "cta",
  "contact",
  "stickybar",
  "footer",
]);

/** 页面里带 data-reveal 的元素滚到视口就淡入上移；没有 IntersectionObserver 或开了减少动效就直接全部显示 */
function useReveal(deps: unknown) {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // 减少动效偏好、不支持 IO、或页面开在后台标签页(浏览器会暂停动画和 IO 回调)：直接全部显示，内容永远不被动效挡住
    if (reduce || document.hidden || !("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        }),
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [deps]);
}

function useFonts(style: string) {
  useEffect(() => {
    const href = fontsHref(style);
    if (document.head.querySelector(`link[data-flash-fonts][href="${href}"]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = href;
    l.setAttribute("data-flash-fonts", "");
    document.head.appendChild(l);
  }, [style]);
}

function FlashSite({ page, appMode = false }: { page: Page; appMode?: boolean }) {
  const sections = page.sections as Record<string, SectionProps>;
  const allTypes = page.types as string[];
  // В рабочем пространстве оставляем только прикладные экраны, без витрины.
  const types = appMode ? allTypes.filter((t) => !MARKETING.has(t)) : allTypes;
  const style = meta.theme?.style || "modern";
  const home = site.pages.find((p) => p.kind === "home");
  const hours = ((home?.sections as Record<string, SectionProps> | undefined)?.map?.hours as Hours | undefined) || [];
  const ctx: PageCtx = {
    lang: meta.lang || "",
    brand: meta.name || "",
    ui: meta.ui || {},
    kind: page.kind,
    navLabel: str(((sections.navbar?.links as { href?: string; label?: string }[] | undefined) || []).find((l) => l.href === page.path)?.label) || undefined,
    style,
    formUrl: meta.formUrl,
    phone: meta.nap?.phone,
    address: meta.nap?.address,
    generated: meta.nap?.generated,
    hours: Array.isArray(hours) ? hours : [],
    ctaHref: types.includes("contact") ? "#contact" : "/contact",
    images: (page as { images?: PageCtx["images"] }).images || {},
  };

  const [js, setJs] = useState(false);
  const { mode } = useThemeMode();
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => setJs(true), []);
  useFonts(style);
  useReveal(page.path + js);
  useEffect(() => {
    applySeo(site as never, page as never, ctx);
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // 区块按位置分三段：导航在前、页脚/手机栏在后，中间是 <main>（主内容地标，跳转链接指向它）
  let muted = false;
  const render = (type: string) => {
    const C = RENDERERS[type];
    let props = sections[type];
    if (!C || !props) return null;
    if (ALTERNATING.has(type)) {
      props = { ...props, tone: muted ? "muted" : "default" };
      muted = !muted;
    } else {
      muted = false;
    }
    return <C key={type} {...props} />;
  };
  const head: string[] = appMode ? [] : types.filter((t) => t === "announcement" || t === "navbar");
  const tail: string[] = types.filter((t) => t === "footer" || t === "stickybar");
  const body = types.filter((t) => !head.includes(t) && !tail.includes(t));
  return (
    <PageContext.Provider value={ctx}>
      <div
        ref={topRef}
        className={`flash-site flex w-full flex-col max-md:pb-20${js ? " flash-js" : ""}${appMode ? " flash-app" : ""}`}
        data-style={style}
        style={themeStyle(meta.theme?.accent || "", mode, style)}
        dir={isRtl(ctx.lang) ? "rtl" : "ltr"}
      >
        <a href="#main" className="flash-skip">
          {str(ctx.ui.skip_to_content) || "Skip to content"}
        </a>
        {appMode ? (
          <AppShell sections={sections} />
        ) : (
          <>
            {head.map(render)}
            <main id="main" className="flex flex-col">
              {body.map(render)}
            </main>
            {tail.map(render)}
          </>
        )}
        <FeedbackWidget />
      </div>
    </PageContext.Provider>
  );
}

function Splash() {
  const style = meta.theme?.style || "modern";
  const { mode } = useThemeMode();
  useFonts(style);
  return (
    <div
      className="flash-site flex min-h-screen w-full items-center justify-center bg-background text-foreground"
      data-style={style}
      style={themeStyle(meta.theme?.accent || "", mode, style)}
    >
      <div className="flex flex-col items-center gap-4">
        <span className="size-9 animate-spin rounded-full border-2 border-primary/25 border-t-primary" aria-hidden />
        <span className="text-sm text-muted-foreground">{t("Проверяем вход…")}</span>
      </div>
    </div>
  );
}

/** Вход по Google: без сессии показываем компактную главную, с сессией — рабочий экран и меню разделов. */
export default function FlashPage({ page }: { page: Page }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Landing />;
  return <FlashSite page={page} appMode />;
}
